import { useEffect, useState, useCallback } from "react";
import { FaLayerGroup, FaCalendarAlt, FaRunning } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import KpiCard from "../../NewDashboard/KpiCard";
import "../../NewDashboard/style.css";
import {
  getAcademyDashboardStats,
  getCoachAthleteStats,
  getCoachAthletesExport,
} from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import { getAllSportList, getAllDistrictList } from "../../../../services/commonApiService/CommonApiService";
import CoachAthletesModal from "./CoachAthletesModal";

const PAGE_SIZE = 10;

const ATHLETE_COUNT_OPTIONS = [
  { value: "", label: "All Athletes" },
  { value: "lt15", label: "< 15 Athletes" },
  { value: "lt30", label: "< 30 Athletes" },
];

const escapeCsv = (val) => {
  const s = String(val ?? "");
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

const AcademyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const [clusterFilter, setClusterFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [athleteCountFilter, setAthleteCountFilter] = useState("");

  const [districtList, setDistrictList] = useState([]);
  const [sportsList, setSportsList] = useState([]);

  useEffect(() => {
    getAllDistrictList(4016)
      .then((res) => setDistrictList(res?.data || []))
      .catch(() => setDistrictList([]));


    getAllSportList()
      .then((res) => setSportsList(res?.data?.data || []))
      .catch(() => setSportsList([]));

    getAcademyDashboardStats()
      .then((res) => setStats(res?.data?.data ?? null))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const fetchCoaches = useCallback(() => {
    setCoachesLoading(true);
    setPage(1);
    const params = {};
    if (clusterFilter) params.cluster = clusterFilter;
    if (sportFilter)   params.sport_id = sportFilter;
    if (athleteCountFilter) params.athlete_count = athleteCountFilter;

    getCoachAthleteStats(params)
      .then((res) => setCoaches(res?.data?.data ?? []))
      .catch(() => setCoaches([]))
      .finally(() => setCoachesLoading(false));
  }, [clusterFilter, sportFilter, athleteCountFilter]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (clusterFilter)      params.cluster       = clusterFilter;
      if (sportFilter)        params.sport_id      = sportFilter;
      if (athleteCountFilter) params.athlete_count = athleteCountFilter;
      const res = await getCoachAthletesExport(params);
      const rows = res?.data?.data ?? [];
      const header = ["Coach Name", "Cluster", "Sports", "Total Athletes", "Athlete Name", "Phone Number"];
      let lastCoach = null;
      const dataLines = rows.map((r) => {
        const isFirst = r.coach_name !== lastCoach;
        lastCoach = r.coach_name;
        return [
          isFirst ? r.coach_name : "",
          isFirst ? (r.cluster || "") : "",
          isFirst ? r.sports : "",
          isFirst ? r.athlete_count : "",
          r.athlete_name,
          r.athlete_mobile,
        ]
          .map(escapeCsv)
          .join(",");
      });
      const lines = [header.join(","), ...dataLines];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "coach_athletes.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const paginated = coaches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="container-fluid p-4">
      <h4 className="fw-bold mb-4">Academy Dashboard</h4>

      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-md-6 col-lg-4 d-flex">
          <KpiCard
            title="Number of Batches"
            value={stats?.batches_count ?? 0}
            icon={<FaLayerGroup size={22} color="#8757e8" />}
            loading={statsLoading}
          />
        </div>
        <div className="col-12 col-md-6 col-lg-4 d-flex">
          <KpiCard
            title="Training Sessions (This Month)"
            value={stats?.monthly_trainings_count ?? 0}
            icon={<FaCalendarAlt size={22} color="#8757e8" />}
            loading={statsLoading}
          />
        </div>
        <div className="col-12 col-md-6 col-lg-4 d-flex">
          <KpiCard
            title="Total Athletes Mapped"
            value={stats?.total_athletes_mapped ?? 0}
            icon={<FaRunning size={22} color="#8757e8" />}
            loading={statsLoading}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <select
            className="form-select"
            value={clusterFilter}
            onChange={(e) => setClusterFilter(e.target.value)}
          >
            <option value="">All Clusters</option>
            {districtList.map((d) => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-3">
          <select
            className="form-select"
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value)}
          >
            <option value="">All Sports</option>
            {sportsList.map((s) => (
              <option key={s.id} value={s.sport_id}>{s.sport_name}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-3">
          <select
            className="form-select"
            value={athleteCountFilter}
            onChange={(e) => setAthleteCountFilter(e.target.value)}
          >
            {ATHLETE_COUNT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Coach-Athletes Table */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-1 flex-wrap gap-2">
        <span className="fw-semibold text-muted" style={{ fontSize: "14px" }}>Coach Overview</span>
        <button
          className="btn btn-sm"
          style={{ background: "#8757e8", color: "#fff", borderRadius: "8px", whiteSpace: "nowrap" }}
          onClick={handleExportCsv}
          disabled={exporting}
        >
          {exporting ? "Exporting..." : "⬇ Export CSV"}
        </button>
      </div>
      <div className="card-box p-0" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table className="table mb-0" style={{ width: "100%" }}>
          <thead>
            <tr style={{ background: "#8757e8" }}>
              <th className="text-white fw-semibold py-2 px-3">Coach Name</th>
              <th className="text-white fw-semibold py-2 px-3 d-none d-md-table-cell">Cluster</th>
              <th className="text-white fw-semibold py-2 px-3 d-none d-md-table-cell">Sport</th>
              <th className="text-white fw-semibold py-2 px-3">Athletes</th>
              <th className="text-white fw-semibold py-2 px-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {coachesLoading ? (
              <tr>
                <td colSpan="5" className="text-center py-4">Loading...</td>
              </tr>
            ) : paginated.length > 0 ? (
              paginated.map((coach, idx) => (
                <tr key={`${coach.id}-${coach.cluster}-${idx}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td className="py-2 px-3 fw-semibold" style={{ maxWidth: "140px", wordBreak: "break-word" }}>
                    {coach.coach_name?.trim() || "—"}
                    {/* Show cluster/sport as subtitle on mobile */}
                    <div className="d-md-none text-muted" style={{ fontSize: "11px", fontWeight: 400 }}>
                      {[coach.cluster, coach.sport_name].filter(Boolean).join(" · ") || ""}
                    </div>
                  </td>
                  <td className="py-2 px-3 d-none d-md-table-cell">{coach.cluster || "—"}</td>
                  <td className="py-2 px-3 d-none d-md-table-cell">{coach.sport_name || "—"}</td>
                  <td className="py-2 px-3 fw-semibold">{coach.athlete_count}</td>
                  <td className="py-2 px-3">
                    <button
                      className="btn btn-light btn-sm"
                      style={{ borderRadius: "8px" }}
                      onClick={() => setSelectedCoach(coach)}
                      title="View Athletes"
                    >
                      <FaEye size={15} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4">No coaches found</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {coaches.length > PAGE_SIZE && (
          <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ borderTop: "1px solid #f0f0f0" }}>
            <span className="text-muted" style={{ fontSize: "14px" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, coaches.length)} of {coaches.length}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-light btn-sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                style={{ borderRadius: "8px" }}
              >
                Previous
              </button>
              <button
                className="btn btn-light btn-sm"
                disabled={page * PAGE_SIZE >= coaches.length}
                onClick={() => setPage((p) => p + 1)}
                style={{ borderRadius: "8px" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedCoach && (
        <CoachAthletesModal
          coach={selectedCoach}
          onClose={() => setSelectedCoach(null)}
        />
      )}
    </div>
  );
};

export default AcademyDashboard;
